import React, { useState } from 'react';
import { Database, Upload, Check, X, AlertCircle, Download } from 'lucide-react';
import MigrationService from '../utils/migrationService';
import { authAPI } from '../api/auth';

export default function MigrationPanel() {
  const [status, setStatus] = useState('ready'); // ready, migrating, success, error
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState(null);
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });

  const handleMigration = async () => {
    try {
      setStatus('migrating');
      setProgress('Iniciando migración...');

      // 1. Login
      setProgress('🔐 Autenticando...');
      await authAPI.login(credentials.username, credentials.password);

      // 2. Ejecutar migración
      setProgress('📦 Migrando datos...');
      const migration = new MigrationService();
      const result = await migration.runFullMigration();

      if (result.success) {
        setStatus('success');
        setResults(result);
      } else {
        setStatus('error');
        setResults(result);
      }

    } catch (error) {
      setStatus('error');
      setResults({ error: error.message });
      console.error('Error en migración:', error);
    }
  };

  const exportLocalStorageBackup = () => {
    const backup = {
      timestamp: new Date().toISOString(),
      data: {
        employees: localStorage.getItem('7family_employees'),
        departments: localStorage.getItem('7family_departments'),
        positions: localStorage.getItem('7family_positions'),
        shifts: localStorage.getItem('7family_shifts'),
        schedules: localStorage.getItem('7family_schedules'),
        vacaciones: localStorage.getItem('7family_vacaciones'),
        permisos: localStorage.getItem('7family_permisos'),
        lunch_menus: localStorage.getItem('7family_lunch_menus'),
        lunch_selections: localStorage.getItem('7family_lunch_selections')
      }
    };

    const blob = new Blob([JSON.stringify(backup, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `7family_backup_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-purple-50 to-pink-50 p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-4 bg-gradient-to-br from-purple-500 to-pink-500 rounded-xl">
              <Database className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">
                Migración de Datos
              </h1>
              <p className="text-gray-600">
                LocalStorage → PostgreSQL Database
              </p>
            </div>
          </div>

          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800">
                <p className="font-semibold mb-1">⚠️ Importante:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Esta acción migrará todos los datos de localStorage a PostgreSQL</li>
                  <li>Asegúrate de tener el backend corriendo en http://localhost:5000</li>
                  <li>Recomendamos hacer un backup antes de continuar</li>
                  <li>Los datos en localStorage NO se borrarán automáticamente</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Backup Section */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">
            1. Backup de Seguridad
          </h2>
          <p className="text-gray-600 mb-4">
            Descarga una copia de seguridad de tus datos actuales en localStorage
          </p>
          <button
            onClick={exportLocalStorageBackup}
            className="px-6 py-3 bg-blue-600 text-white rounded-xl font-semibold flex items-center gap-2 hover:bg-blue-700 transition-colors"
          >
            <Download className="w-5 h-5" />
            Descargar Backup JSON
          </button>
        </div>

        {/* Login Section */}
        {status === 'ready' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              2. Credenciales de Administrador
            </h2>
            <p className="text-gray-600 mb-6">
              Ingresa las credenciales de un usuario con permisos de administrador
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Usuario
                </label>
                <input
                  type="text"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  placeholder="admin"
                  className="w-full px-4 py-3 border-2 rounded-xl focus:border-purple-500 outline-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Contraseña
                </label>
                <input
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  placeholder="••••••••"
                  className="w-full px-4 py-3 border-2 rounded-xl focus:border-purple-500 outline-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* Migration Button */}
        {status === 'ready' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              3. Iniciar Migración
            </h2>
            <button
              onClick={handleMigration}
              disabled={!credentials.username || !credentials.password}
              className="w-full px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold text-lg flex items-center justify-center gap-3 hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-6 h-6" />
              Iniciar Migración a PostgreSQL
            </button>
          </div>
        )}

        {/* Progress */}
        {status === 'migrating' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center">
              <div className="inline-block p-4 bg-purple-100 rounded-full mb-4">
                <div className="animate-spin">
                  <Database className="w-12 h-12 text-purple-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Migrando Datos...
              </h2>
              <p className="text-gray-600">{progress}</p>
              <div className="mt-6 h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-purple-600 to-pink-600 animate-pulse" style={{ width: '100%' }}></div>
              </div>
            </div>
          </div>
        )}

        {/* Success */}
        {status === 'success' && results && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center mb-6">
              <div className="inline-block p-4 bg-green-100 rounded-full mb-4">
                <Check className="w-12 h-12 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                ¡Migración Completada!
              </h2>
              <p className="text-gray-600">
                Los datos han sido migrados exitosamente a PostgreSQL
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-green-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-green-600">{results.stats.successful}</p>
                <p className="text-sm text-gray-600">Exitosos</p>
              </div>
              <div className="bg-red-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-red-600">{results.stats.errors}</p>
                <p className="text-sm text-gray-600">Errores</p>
              </div>
              <div className="bg-yellow-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-yellow-600">{results.stats.skipped}</p>
                <p className="text-sm text-gray-600">Omitidos</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-blue-600">{results.stats.duration}s</p>
                <p className="text-sm text-gray-600">Duración</p>
              </div>
            </div>

            {results.results.errors.length > 0 && (
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
                <h3 className="font-bold text-red-800 mb-2">Errores encontrados:</h3>
                <ul className="text-sm text-red-700 space-y-1">
                  {results.results.errors.map((err, i) => (
                    <li key={i}>• {err}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => window.location.href = '/'}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-semibold hover:shadow-lg transition-all"
              >
                Ir al Dashboard
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-300 transition-colors"
              >
                Migrar Nuevamente
              </button>
            </div>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center">
              <div className="inline-block p-4 bg-red-100 rounded-full mb-4">
                <X className="w-12 h-12 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">
                Error en la Migración
              </h2>
              <p className="text-gray-600 mb-6">
                {results?.error || 'Ocurrió un error durante la migración'}
              </p>
              <button
                onClick={() => setStatus('ready')}
                className="px-6 py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-colors"
              >
                Intentar Nuevamente
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
